/* FM · header behaviour: ICONS top bar, announcement marquee, search hints, mega menu hover. */
(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const storage = {
    get(key) {
      try {
        return window.localStorage.getItem(key);
      } catch (e) {
        return null;
      }
    },
    set(key, value) {
      try {
        window.localStorage.setItem(key, value);
      } catch (e) {
        /* storage unavailable: the bar simply stays open */
      }
    },
  };

  if (!customElements.get('fm-icons-topbar')) {
    customElements.define(
      'fm-icons-topbar',
      class FmIconsTopbar extends HTMLElement {
        static KEY = 'fmIconsTopbarClosed';

        connectedCallback() {
          if (this.dataset.dismissible !== 'true') return;
          this.bar = this.querySelector('[data-fm-topbar-bar]');
          this.closeButton = this.querySelector('[data-fm-topbar-close]');
          this.openButton = this.querySelector('[data-fm-topbar-open]');
          if (!this.bar || !this.closeButton || !this.openButton) return;

          this.closeButton.addEventListener('click', () => this.setClosed(true, true));
          this.openButton.addEventListener('click', () => this.setClosed(false, true));
          if (storage.get(FmIconsTopbar.KEY) === '1') this.setClosed(true, false);
        }

        setClosed(closed, remember) {
          this.bar.hidden = closed;
          this.openButton.hidden = !closed;
          this.classList.toggle('is-closed', closed);
          if (remember) {
            storage.set(FmIconsTopbar.KEY, closed ? '1' : '0');
            (closed ? this.openButton : this.closeButton).focus();
          }
        }
      }
    );
  }

  if (!customElements.get('fm-marquee')) {
    customElements.define(
      'fm-marquee',
      class FmMarquee extends HTMLElement {
        connectedCallback() {
          this.toggle = this.querySelector('[data-fm-marquee-toggle]');
          if (!this.toggle) return;
          this.toggle.addEventListener('click', () => {
            const paused = !this.classList.contains('is-paused');
            this.classList.toggle('is-paused', paused);
            this.toggle.setAttribute('aria-pressed', String(paused));
            this.toggle.setAttribute('aria-label', paused ? this.toggle.dataset.labelPlay : this.toggle.dataset.labelPause);
          });
        }
      }
    );
  }

  if (!customElements.get('fm-search-hints')) {
    customElements.define(
      'fm-search-hints',
      class FmSearchHints extends HTMLElement {
        static MAX_ROUNDS = 3;

        connectedCallback() {
          this.hints = (this.dataset.hints || '')
            .split('|')
            .map((hint) => hint.trim())
            .filter(Boolean);
          this.prefix = this.dataset.prefix || '';
          this.input = this.querySelector('input[type="search"]');
          const host = this.closest('details-modal');
          this.pill = host ? host.querySelector('[data-fm-search-pill]') : null;
          if (!this.input || this.hints.length < 2) return;

          this.index = 0;
          this.rounds = 0;
          this.schedule(2500);
        }

        disconnectedCallback() {
          clearTimeout(this.timer);
        }

        schedule(delay) {
          clearTimeout(this.timer);
          this.timer = setTimeout(() => this.next(), delay);
        }

        render(text) {
          const value = `${this.prefix} ${text}`.trim();
          this.input.setAttribute('placeholder', value);
          if (this.pill) this.pill.textContent = value;
        }

        next() {
          this.index = (this.index + 1) % this.hints.length;
          if (this.index === 0) this.rounds += 1;
          if (this.rounds >= FmSearchHints.MAX_ROUNDS) {
            this.render(this.hints[0]);
            return;
          }
          const hint = this.hints[this.index];
          if (reducedMotion.matches || document.hidden) {
            this.render(hint);
            this.schedule(3500);
            return;
          }
          this.type(hint, 0);
        }

        type(hint, length) {
          this.render(hint.slice(0, length));
          if (length < hint.length) {
            this.timer = setTimeout(() => this.type(hint, length + 1), 55);
          } else {
            this.schedule(2200);
          }
        }
      }
    );
  }

  const initMegaHover = () => {
    const finePointer = window.matchMedia('(hover: hover) and (pointer: fine) and (min-width: 990px)');
    document.querySelectorAll('.fm-header details.mega-menu, .fm-header .header__inline-menu details').forEach((details) => {
      if (details.dataset.fmHover) return;
      details.dataset.fmHover = 'true';
      const item = details.closest('li') || details;
      let closeTimer;

      item.addEventListener('mouseenter', () => {
        if (!finePointer.matches) return;
        clearTimeout(closeTimer);
        if (!details.open) details.querySelector('summary')?.click();
      });
      item.addEventListener('mouseleave', () => {
        if (!finePointer.matches) return;
        closeTimer = setTimeout(() => {
          if (details.open) details.querySelector('summary')?.click();
        }, 150);
      });
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initMegaHover);
  } else {
    initMegaHover();
  }
  document.addEventListener('shopify:section:load', initMegaHover);
})();

/* FM · Mega menu promo card follows the hovered link: shows the top product (first available
   product with an image, in the collection's sort order) of the hovered collection. Products come
   from /collections/<handle>/products.json, fetched once per collection (preloaded when a panel
   opens). The panel's default card comes back every time the panel reopens. */
(() => {
  const SEL = '.fm-mega a[data-fm-promo-collection]';
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const cache = new Map();
  const sized = (src, w) => src + (src.includes('?') ? '&' : '?') + 'width=' + w;
  const getTop = (handle) => {
    if (!cache.has(handle)) {
      cache.set(
        handle,
        fetch(root + 'collections/' + encodeURIComponent(handle) + '/products.json?limit=8')
          .then((r) => (r.ok ? r.json() : { products: [] }))
          .then(({ products }) => {
            const p = (products || []).find((x) => x.images.length && x.variants.some((v) => v.available));
            if (!p) return null;
            const src = p.images[0].src;
            return {
              title: p.title,
              url: root + 'products/' + p.handle,
              img: sized(src, 640),
              srcset: [320, 480, 640].map((w) => sized(src, w) + ' ' + w + 'w').join(', '),
            };
          })
          .catch(() => null)
      );
    }
    return cache.get(handle);
  };
  const preload = (details) => {
    if (details.dataset.fmPreloaded) return;
    details.dataset.fmPreloaded = 'true';
    details.querySelectorAll(SEL).forEach((a) => {
      getTop(a.dataset.fmPromoCollection).then((t) => {
        if (!t) return;
        const img = new Image();
        img.sizes = '280px';
        img.srcset = t.srcset;
        img.src = t.img;
      });
    });
  };
  const apply = (card, t) => {
    if (!card.fmDefault) card.fmDefault = { html: card.innerHTML, href: card.getAttribute('href') };
    const img = card.querySelector('.fm-mega-promo__image');
    if (img) {
      img.classList.add('is-swapping');
      img.onload = () => img.classList.remove('is-swapping');
      img.loading = 'eager';
      img.removeAttribute('width');
      img.removeAttribute('height');
      img.sizes = '280px';
      img.srcset = t.srcset;
      img.src = t.img;
      img.alt = t.title;
      if (img.complete) img.classList.remove('is-swapping');
    }
    const title = card.querySelector('.fm-mega-promo__title');
    if (title) title.textContent = t.title;
    const price = card.querySelector('.fm-mega-promo__price');
    if (price) price.hidden = true;
    const label = card.querySelector('.fm-mega-promo__cta-label');
    if (label && card.dataset.fmProductCta) label.textContent = card.dataset.fmProductCta;
    card.setAttribute('href', t.url);
  };
  const onEnter = (event) => {
    const a = event.target.closest?.(SEL);
    if (!a) return;
    const card = a.closest('.fm-mega')?.querySelector('.fm-mega-promo');
    if (!card) return;
    const handle = a.dataset.fmPromoCollection;
    if (card.dataset.fmWant === handle) return;
    card.dataset.fmWant = handle;
    getTop(handle).then((t) => {
      if (t && card.dataset.fmWant === handle) apply(card, t);
    });
  };
  const restore = (details) => {
    const card = details.querySelector('.fm-mega-promo');
    if (!card) return;
    delete card.dataset.fmWant;
    if (!card.fmDefault) return;
    card.innerHTML = card.fmDefault.html;
    card.setAttribute('href', card.fmDefault.href);
  };
  document.addEventListener('mouseover', onEnter);
  document.addEventListener('focusin', onEnter);
  document.addEventListener(
    'toggle',
    (event) => {
      const details = event.target;
      if (!details.matches?.('details.mega-menu') || !details.open) return;
      restore(details);
      preload(details);
    },
    true
  );
})();
